import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import LayoutSelector from 'components/Navbars/ServicePanel/ProtocolModal/components/LayoutSelector';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { Button } from 'reactstrap';
import {
  DragHandle,
  DynamicLayoutContainer,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from './styled';
import { openSideDrawer } from 'store/action/sideDrawerActions';
import { closeSideDrawer } from 'store/action/sideDrawerActions';
import { UncontrolledPopover, PopoverHeader, PopoverBody } from 'reactstrap';
import { FaRegLightbulb } from 'react-icons/fa';
import { OutlineTooltip } from 'components/OutlineTooltip';
import ReactButton from 'components/Buttons/ReactButton';

const Portal = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  const portalRef = useRef(null);

  useEffect(() => {
    const portalRoot =
      document.getElementById('portal-root') ||
      (() => {
        const div = document.createElement('div');
        div.id = 'portal-root';
        document.body.appendChild(div);
        return div;
      })();

    const el = document.createElement('div');
    portalRoot.appendChild(el);
    portalRef.current = el;
    setMounted(true);

    return () => {
      if (portalRef.current) {
        portalRoot.removeChild(portalRef.current);
      }
    };
  }, []);

  return mounted && portalRef.current
    ? createPortal(children, portalRef.current)
    : null;
};

const throttle = (func, limit) => {
  let inThrottle;
  let lastArgs;

  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        if (lastArgs) {
          func.apply(this, lastArgs);
          lastArgs = null;
        }
        inThrottle = false;
      }, limit);
    } else {
      lastArgs = args;
    }
  };
};

export const DynamicLayoutModal = ({
  children,
  show,
  onModalToggle,
  title = '',
  subtitle = '',
  showCloseButton = true,
  backdrop = true,
  closeModal = null,
  hasDynamicLayout = false,
  modalWidth = 800,
  minWidth = 300,
  maxWidth = '50vw',
  isToClickOutsideClose = true,
  layoutWidths = { modal: 800, sidebar: 300 },
  createSubProtocol = null,
  defaultFormData,
  createdFrom = '',
  fatherProtocol = undefined,
  ...props
}) => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const [width, setWidth] = useState(
    layoutWidths[selectedLayout] || modalWidth
  );

  /**
   * Represents the state of the side drawer.
   *
   * @typedef {Object} SideDrawerState
   * @property {boolean} [isDrawerCollapsed]
   * @property {boolean} [isToCloseExtraModal]
   * @property {Object} [data]
   * @property {string|undefined} [data.tabId] - O id da tab selecionada, se existir
   */
  /** @type {SideDrawerState} */
  const sideDrawer = useSelector((state) => state.sideDrawer);

  /**
   * @typedef {'modal' | 'sidebar' | 'sidedrawer'} SelectedLayout
   */
  /** @type {[SelectedLayout, React.Dispatch<React.SetStateAction<SelectedLayout>>]} */
  const [selectedLayout, setSelectedLayout] = useState(() => {
    if (sideDrawer?.data?.tabId === 3) return 'sidedrawer';

    return localStorage.getItem('selectedLayout') || 'modal';
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (createSubProtocol === false) {
      setWidth(1500);
    } else {
      setWidth(
        selectedLayout === 'modal' ? layoutWidths.modal : layoutWidths.sidebar
      );
    }
  }, [createSubProtocol]);

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isResizingRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (selectedLayout === 'modal') {
      if (show) {
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
    }

    if (selectedLayout === 'sidedrawer' && show) {
      dispatch(
        openSideDrawer({
          tabId: 3,
          answeringDialerCallData: defaultFormData,
          createdFrom,
          fatherProtocol
        })
      );
    }
  }, [show, selectedLayout]);

  const handleLayoutChange = useCallback(
    (layout) => {
      setSelectedLayout(layout);
      setWidth(layoutWidths[layout] || modalWidth);
      localStorage.setItem('selectedLayout', layout);
      layout === 'sidedrawer'
        ? dispatch(
            openSideDrawer({
              tabId: 3,
              answeringDialerCallData: defaultFormData,
              createdFrom,
              fatherProtocol
            })
          )
        : dispatch(closeSideDrawer({ tabId: 3 }));
    },
    [layoutWidths, modalWidth]
  );

  useEffect(() => {
    if (sideDrawer?.data?.tabId === 3 && show) {
      return onModalToggle(false);
    } else if (sideDrawer?.data?.tabId === 0 && sideDrawer.isDrawerCollapsed) {
      setSelectedLayout(localStorage.getItem('selectedLayout') || 'modal');
      dispatch(closeSideDrawer({ tabId: 0 }));

      return onModalToggle(true);
    } else {
      onModalToggle(show);
    }
  }, [show, onModalToggle, sideDrawer]);

  const [showConfirm, setShowConfirm] = useState(false);
  const closeBtnRef = useRef();

  const handleClose = useCallback(() => {
    if (selectedLayout === 'modal') {
      setShowConfirm(true);
      return;
    }
    if (closeModal) {
      closeModal();
    } else {
      return onModalToggle(false);
    }
  }, [closeModal, selectedLayout]);

  const handleConfirmClose = () => {
    setShowConfirm(false);
    if (closeModal) {
      closeModal();
    } else {
      onModalToggle(false);
    }
  };

  const handleCancelClose = () => {
    setShowConfirm(false);
  };

  const handleBackdropClick = useCallback(
    (e) => {
      if (isToClickOutsideClose) {
        handleClose();
        return;
      }
      e.stopPropagation();
    },
    [isToClickOutsideClose, handleClose]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (selectedLayout === 'sidebar') {
        isResizingRef.current = true;
        startXRef.current = e.clientX;
        startWidthRef.current = width;
        document.body.classList.add('resize-active');
        e.preventDefault();
      }
    },
    [selectedLayout, width]
  );

  const handleMouseMove = useCallback(
    throttle((e) => {
      if (!isResizingRef.current) return;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = startXRef.current - e.clientX;
        const newWidth = Math.max(
          minWidth,
          Math.min(maxWidth, startWidthRef.current + deltaX)
        );

        if (containerRef.current) {
          containerRef.current.style.width = `${newWidth}px`;
        }
      });
    }, 16),
    [minWidth, maxWidth]
  );

  const handleMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    document.body.classList.remove('resize-active');

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    if (containerRef.current) {
      const finalWidth = parseInt(containerRef.current.style.width, 10);
      layoutWidths[selectedLayout] = finalWidth;
      setWidth(finalWidth);
    }
  }, [selectedLayout, layoutWidths]);

  useEffect(() => {
    if (!show) return;

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [show, handleMouseMove, handleMouseUp, handleClose]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (selectedLayout === 'sidebar' && containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

        if (scrollHeight > clientHeight) {
          const isScrollingDown = e.deltaY > 0;
          const isScrollingUp = e.deltaY < 0;

          const isAtBottom = scrollTop + clientHeight >= scrollHeight;
          const isAtTop = scrollTop <= 0;

          if ((isScrollingDown && !isAtBottom) || (isScrollingUp && !isAtTop)) {
            e.preventDefault();
            containerRef.current.scrollTop += e.deltaY;
          }
        }
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, [selectedLayout]);

  if (!show) return null;

  const content = (
    <>
      {backdrop && selectedLayout === 'modal' && (
        <ModalOverlay onClick={handleBackdropClick} />
      )}
      <DynamicLayoutContainer
        ref={containerRef}
        layout={selectedLayout}
        width={width}
        style={{ width: `${width}px` }}
        {...props}
      >
        <ModalHeader>
          <div>
            <h6 className="modal-title">{title}</h6>
            {subtitle && <p>{subtitle}</p>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            {hasDynamicLayout && (
              <LayoutSelector
                selectedLayout={selectedLayout}
                onLayoutChange={handleLayoutChange}
                systemConfiguration={systemConfiguration}
              />
            )}
            <button
              aria-label="Close"
              className="close"
              id="closeModalBtn"
              type="button"
              ref={closeBtnRef}
              onClick={handleClose}
              style={{ marginLeft: '10px' }}
            >
              <span aria-hidden={true}>×</span>
            </button>
            {showConfirm && selectedLayout === 'modal' && (
              <UncontrolledPopover
                trigger="legacy"
                placement="bottom"
                target="closeModalBtn"
                isOpen={showConfirm}
                toggle={handleCancelClose}
              >
                <PopoverHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Tem certeza?</span>
                    <div>
                      <FaRegLightbulb
                        cursor="help"
                        id="UncontrolledTooltipExample"
                      />
                      <OutlineTooltip
                        placement="right"
                        target="UncontrolledTooltipExample"
                      >
                        <span className="small">
                          Dica: se precisar de mais espaço na tela você pode
                          minimizar esta janela clicando na aba lateral "Criar
                          protocolo".
                        </span>
                      </OutlineTooltip>
                    </div>
                  </div>
                </PopoverHeader>
                <PopoverBody>
                  Ao fechar a janela os dados atuais não poderão mais ser
                  visualizados.
                  <div
                    style={{
                      fontSize: 14,
                      marginTop: 18,
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    <ReactButton
                      type="button"
                      btnColor="confirmation"
                      onClick={handleConfirmClose}
                      size="sm"
                      style={{ minWidth: 140, width: '100%' }}
                    >
                      Fechar mesmo assim
                    </ReactButton>
                  </div>
                </PopoverBody>
              </UncontrolledPopover>
            )}
          </div>
        </ModalHeader>

        <ModalBody>{children}</ModalBody>

        {showCloseButton && (
          <ModalFooter>
            <Button
              style={buttonStyle(systemConfiguration.cancelation_button_color)}
              onClick={handleClose}
            >
              Fechar
            </Button>
          </ModalFooter>
        )}

        <DragHandle layout={selectedLayout} onMouseDown={handleMouseDown} />
      </DynamicLayoutContainer>
    </>
  );

  return selectedLayout === 'sidedrawer' ? null : <Portal>{content}</Portal>;
};
